import { AcademicPeriodType } from 'app/main/academic-period-tree';
import { ContentType } from 'app/main/school-course/syllabus/syllabus.model';
import { Subject } from 'app/main/shared/subjects/subject.model';
import { Period, TimePeriod } from 'app/main/time-periods/time-periods.model';

import { CourseType } from '../enum/courseType.enum';
import { GradeType } from '../enum/gradeType.enum';
import { GradeExcuse } from '../grade-excuse-dialog/grade-excuse.model';
import { BBBServerSetting } from './../../schools/model/bbb-server-setting.model';
import { ColorPalette } from './color-palette.model';
import { CourseLog } from './course-log.model';
import { StudentAttendanceDate } from './student-attendance.model';
import { TranslatableString } from './translateble-string.model';

export const CourseFilterType = {
    TEACHER: 'TEACHER',
    STUDENT: 'STUDENT',
    CLAZZ: 'CLAZZ',
    COURSE: 'COURSE',
    SUBJECT: 'SUBJECT',
    LOCATION: 'LOCATION',
} as const;
export type CourseFilterType = (typeof CourseFilterType)[keyof typeof CourseFilterType];

export const CourseDeliveryMethod = {
    ONLINE: 'ONLINE',
    FACE: 'FACE',
    BLENDED: 'BLENDED',
    DISTANCE: 'DISTANCE',
} as const;
export type CourseDeliveryMethod = (typeof CourseDeliveryMethod)[keyof typeof CourseDeliveryMethod];

export interface ICourse {
    id: string;
    schoolId?: string;
    name?: string;
    code?: string;
    reportCardDescription?: string;
    translateName?: TranslatableString[];
    subject?: Subject;
    gradeLevelId?: string;
    gradeLevelName?: string;
    academicPeriodId?: string;
    semesterId?: string;
    primaryTeacherId?: string;
    secondaryTeacherId?: string;
    locationId?: string;
    locationName?: string;
    type?: CourseType;
    totalLessonsPerWeek?: number;
    maxLessonsPerDay?: number;
    bbbServerSetting?: BBBServerSetting;
    courseLogs?: CourseLog[];
    filled?: boolean;
    attendeeCount?: number;
    teacherFirstName?: string;
    teacherLastName?: string;
    teacherMiddleName?: string;
    secondTeacherFirstName?: string;
    secondTeacherLastName?: string;
    secondTeacherMiddleName?: string;
    groups?: Group[];
    gradeSettings?: CourseGradeSettings;
    notificationSettings?: NotificationsSettings;
    enableAttendance?: boolean;
    singleUserSession?: boolean;
    syllabusOverallProgress?: number;
    studentProgressList?: StudentProgress[];
    markedStudents?: string[];
    hideSyllabusFromRepresentatives?: boolean;
    scheduleLocationId?: boolean;
    selfEnrollmentSettings?: SelfEnrollmentSettings;
    courseSelfEnrollment?: CourseSelfEnrollment;
    studentGradeProgress?: number;
    className?: string;
    image?: string;
    imageUrl?: string;
    dataLanguage?: string;
    excludeSyllabusProgressOverdueAssignments?: boolean;
    googleClassroomCourseId?: string;
    googleClassroomLink?: string;
    classId?: string;
    section?: string;
    termCode?: string;
    deliveryMethod?: CourseDeliveryMethod;
    archived?: boolean;
}
export class Course implements ICourse {
    constructor(
        public id: string,
        public schoolId?: string,
        public name?: string,
        public code?: string,
        public translateName?: TranslatableString[],
        public subject?: Subject,
        public primaryTeacherId?: string,
        public secondaryTeacherId?: string,
        public gradeLevelId?: string,
        public gradeLevelName?: string,
        public academicPeriodId?: string,
        public semesterId?: string,
        public locationId?: string,
        public type?: CourseType,
        public totalLessonsPerWeek?: number,
        public maxLessonsPerDay?: number,
        public bbbServerSetting?: BBBServerSetting,
        public courseLogs?: CourseLog[],
        public attendeeCount?: number,
        public filled?: boolean, // used in timeTable
        public teacherFirstName?: string,
        public teacherLastName?: string,
        public teacherMiddleName?: string,
        public secondTeacherFirstName?: string,
        public secondTeacherLastName?: string,
        public secondTeacherMiddleName?: string,
        public groups?: Group[],
        public gradeSettings?: CourseGradeSettings,
        public notificationSettings?: NotificationsSettings,
        public firstImageUrl?: string,
        public secondImageUrl?: string,
        public enableAttendance?: boolean,
        public singleUserSession?: boolean,
        public studentProgressList?: StudentProgress[],
        public markedStudents?: string[],
        public hideSyllabusFromRepresentatives?: boolean,
        public selfEnrollmentSettings?: SelfEnrollmentSettings,
        public courseSelfEnrollment?: CourseSelfEnrollment,
        public classId?: string,
        public imageUrl?: string,
        public dataLanguage?: string,
        public excludeSyllabusProgressOverdueAssignments?: boolean,
        public googleClassroomCourseId?: string,
        public googleClassroomLink?: string,
        public reportCardDescription?: string,
        public section?: string,
        public termCode?: string,
        public deliveryMethod?: CourseDeliveryMethod,
        public archived?: boolean
    ) {
        this.id = id ?? null;
        this.schoolId = schoolId ?? null;
        this.name = name ?? '';
        this.code = code ?? '';
        this.translateName = translateName ?? [];
        this.subject = subject ?? new Subject(null);
        this.primaryTeacherId = primaryTeacherId ?? null;
        this.secondaryTeacherId = secondaryTeacherId ?? null;
        this.gradeLevelId = gradeLevelId ?? null;
        this.gradeLevelName = gradeLevelName ?? null;
        this.academicPeriodId = academicPeriodId ?? null;
        this.semesterId = semesterId ?? null;
        this.locationId = locationId ?? null;
        this.type = type ?? null;
        this.totalLessonsPerWeek = totalLessonsPerWeek ?? 0;
        this.maxLessonsPerDay = maxLessonsPerDay ?? 0;
        this.bbbServerSetting = bbbServerSetting ?? null;
        this.courseLogs = courseLogs ?? [];
        this.filled = filled ?? false;
        this.attendeeCount = attendeeCount ?? 0;
        this.teacherFirstName = teacherFirstName ?? '';
        this.teacherLastName = teacherLastName ?? '';
        this.teacherMiddleName = teacherMiddleName ?? '';
        this.secondTeacherFirstName = secondTeacherFirstName ?? '';
        this.secondTeacherLastName = secondTeacherLastName ?? '';
        this.secondTeacherMiddleName = secondTeacherMiddleName ?? '';
        this.groups = groups ?? [];
        this.gradeSettings = gradeSettings ?? new CourseGradeSettings();
        this.notificationSettings = notificationSettings ?? new NotificationsSettings();
        this.firstImageUrl = firstImageUrl ?? null;
        this.secondImageUrl = secondImageUrl ?? null;
        this.enableAttendance = enableAttendance ?? true;
        this.singleUserSession = singleUserSession ?? false;
        this.studentProgressList = studentProgressList ?? [];
        this.markedStudents = markedStudents ?? [];
        this.hideSyllabusFromRepresentatives = hideSyllabusFromRepresentatives ?? false;
        this.selfEnrollmentSettings = selfEnrollmentSettings ?? new SelfEnrollmentSettings();
        this.courseSelfEnrollment = courseSelfEnrollment ?? null;
        this.classId = classId ?? null;
        this.imageUrl = imageUrl ? imageUrl : null;
        this.dataLanguage = dataLanguage ?? null;
        this.excludeSyllabusProgressOverdueAssignments = excludeSyllabusProgressOverdueAssignments ?? false;
        this.googleClassroomCourseId = googleClassroomCourseId ?? null;
        this.googleClassroomLink = googleClassroomLink ?? null;
        this.reportCardDescription = reportCardDescription ?? null;
        this.section = section ?? null;
        this.termCode = termCode ?? null;
        this.deliveryMethod = deliveryMethod ?? null;
        this.archived = archived ?? false;
    }
}

export class CourseSearch {
    constructor(
        public schoolId?: string,
        public name?: string,
        public academicYearId?: string,
        public gradeLevelId?: string,
        public subjectId?: string,
        public employeeId?: string,
        public type?: CourseType,
        public tableSubTitle?: string,
        public pageSize?: number,
        public pageIndex?: number,
        public schoolClassId?: string
    ) {
        this.schoolId = schoolId ? schoolId : null;
        this.name = name ? name : null;
        this.academicYearId = academicYearId ? academicYearId : null;
        this.gradeLevelId = gradeLevelId ? gradeLevelId : null;
        this.subjectId = subjectId ? subjectId : null;
        this.employeeId = employeeId ? employeeId : null;
        this.type = type ? type : null;
        this.tableSubTitle = tableSubTitle ?? '';
        this.pageSize = pageSize ?? 10;
        this.pageIndex = pageIndex ?? 0;
        this.schoolClassId = schoolClassId ? schoolClassId : null;
    }
}

export class CourseTimetableItemDTO {
    public constructor(
        public period?: Period,
        public monday?: ICourse[],
        public tuesday?: ICourse[],
        public wednesday?: ICourse[],
        public thursday?: ICourse[],
        public friday?: ICourse[],
        public saturday?: ICourse[],
        public sunday?: ICourse[],
        public a?: ICourse[],
        public b?: ICourse[],
        public c?: ICourse[],
        public d?: ICourse[],
        public e?: ICourse[]
    ) {
        this.period = period ? period : null;
        this.monday = monday ? monday : [];
        this.tuesday = tuesday ? tuesday : [];
        this.wednesday = wednesday ? wednesday : [];
        this.thursday = thursday ? thursday : [];
        this.friday = friday ? friday : [];
        this.saturday = saturday ? saturday : [];
        this.sunday = sunday ? sunday : [];
        this.a = a ? a : [];
        this.b = b ? b : [];
        this.c = c ? c : [];
        this.d = d ? d : [];
        this.e = e ? e : [];
    }
}

export class CourseTimetableDTO {
    constructor(
        public timePeriod?: TimePeriod,
        public items?: CourseTimetableItemDTO[]
    ) {
        this.timePeriod = timePeriod ? timePeriod : null;
        this.items = items ? items : [];
    }
}

export class Group {
    constructor(
        public id?: string,
        public name?: string,
        public studentIds?: string[]
    ) {
        this.id = id ? id : '';
        this.name = name ? name : '';
        this.studentIds = studentIds ? studentIds : [];
    }
}

export class CourseStudentSearch {
    constructor(
        public courseId?: string,
        public academicYearId?: string,
        public groupStudents?: string[]
    ) {
        this.courseId = courseId ? courseId : '';
        this.academicYearId = academicYearId ? academicYearId : '';
        this.groupStudents = groupStudents ? groupStudents : [];
    }
}

export class CourseGradeSettings {
    public constructor(
        public enableGrading?: boolean,
        public excludeFromGPA?: boolean,
        public gradeCategoriesTemplateId?: string,
        public gradeCategoryId?: string,
        public finalGradeTemplateId?: string,
        public gradingSchemeId?: string,
        public credit?: number,
        public allowChangeTermGrade?: boolean,
        public termGradeType?: AcademicPeriodType,
        public excludeFromClassRank?: boolean,
        public excludeFromHonorRoll?: boolean,
        public excludeFromReportCard?: boolean,
        public excludeFromTranscript?: boolean,
        public gradeMultiplier?: number,
        public dualCreditCode?: string,
        public gradingType?: GradingType,
        public educationStandardId?: string
    ) {
        this.enableGrading = enableGrading ? enableGrading : false;
        this.excludeFromGPA = excludeFromGPA ? excludeFromGPA : false;
        this.gradeCategoriesTemplateId = gradeCategoriesTemplateId ? gradeCategoriesTemplateId : null;
        this.gradeCategoryId = gradeCategoryId ? gradeCategoryId : null;
        this.finalGradeTemplateId = finalGradeTemplateId ? finalGradeTemplateId : null;
        this.gradingSchemeId = gradingSchemeId ? gradingSchemeId : null;
        this.credit = credit ? credit : 0;
        this.allowChangeTermGrade = allowChangeTermGrade ? allowChangeTermGrade : false;
        this.termGradeType = termGradeType ? termGradeType : null;
        this.excludeFromClassRank = excludeFromClassRank ?? false;
        this.excludeFromHonorRoll = excludeFromHonorRoll ?? false;
        this.excludeFromReportCard = excludeFromReportCard ?? false;
        this.excludeFromTranscript = excludeFromTranscript ?? false;
        this.gradeMultiplier = gradeMultiplier ?? null;
        this.dualCreditCode = dualCreditCode ?? null;
        this.educationStandardId = this.educationStandardId ?? null;
    }
}
export class NotificationsSettings {
    public constructor(
        public notifyStudentAssignmentPublished?: boolean,
        public notifyStudentViaEmail?: boolean,
        public notifyStudentViaSMS?: boolean,
        public notifyRepresentativesAssignmentPublished?: boolean,
        public notifyRepresentativesViaEmail?: boolean,
        public notifyRepresentativesViaSMS?: boolean,
        public sendSubmitNotification?: boolean,
        public sendLateSubmitNotification?: boolean,
        public sendReviewNotification?: boolean
    ) {
        this.notifyStudentAssignmentPublished = notifyStudentAssignmentPublished
            ? notifyStudentAssignmentPublished
            : false;
        this.notifyStudentViaEmail = notifyStudentViaEmail ? notifyStudentViaEmail : false;
        this.notifyStudentViaSMS = notifyStudentViaSMS ? notifyStudentViaSMS : false;
        this.notifyRepresentativesAssignmentPublished = notifyRepresentativesAssignmentPublished
            ? notifyRepresentativesAssignmentPublished
            : false;
        this.notifyRepresentativesViaEmail = notifyRepresentativesViaEmail ? notifyRepresentativesViaEmail : false;
        this.notifyRepresentativesViaSMS = notifyRepresentativesViaSMS ? notifyRepresentativesViaSMS : false;
        this.sendSubmitNotification = sendSubmitNotification ? sendSubmitNotification : true;
        this.sendLateSubmitNotification = sendLateSubmitNotification ? sendLateSubmitNotification : true;
        this.sendReviewNotification = sendReviewNotification ? sendReviewNotification : true;
    }
}

export class CourseEnrollmentSearch {
    constructor(
        public schoolId?: string,
        public schoolClassId?: string,
        public academicYearId?: string,
        public gradeLevelId?: string
    ) {
        this.schoolId = schoolId ? schoolId : null;
        this.schoolClassId = schoolClassId ? schoolClassId : null;
        this.academicYearId = academicYearId ? academicYearId : null;
        this.gradeLevelId = gradeLevelId ? gradeLevelId : null;
    }
}

export class CourseEnrollment {
    constructor(
        public id?: string,
        public name?: string,
        public subject?: Subject,
        public gradeLevelId?: string,
        public gradeLevelName?: string,
        public teacherFirstName?: string,
        public teacherLastName?: string,
        public teacherMiddleName?: string,
        public maxEnrolledStudentCount?: number,
        public attendeeCount?: number,
        public assignmentCount?: number,
        public selfEnrollmentSettings?: SelfEnrollmentSettings
    ) {
        this.id = id ? id : null;
        this.name = name ? name : null;
        this.subject = subject ? subject : new Subject(null);
        this.gradeLevelId = gradeLevelId ? gradeLevelId : null;
        this.gradeLevelName = gradeLevelName ? gradeLevelName : null;
        this.teacherFirstName = teacherFirstName ? teacherFirstName : null;
        this.teacherLastName = teacherLastName ? teacherLastName : null;
        this.teacherMiddleName = teacherMiddleName ? teacherMiddleName : null;
        this.maxEnrolledStudentCount = maxEnrolledStudentCount ?? 0;
        this.attendeeCount = attendeeCount ?? 0;
        this.assignmentCount = assignmentCount ?? 0;
        this.selfEnrollmentSettings = selfEnrollmentSettings ?? null;
    }
}

export class StudentProgress {
    constructor(
        public courseId?: string,
        public studentId?: string,
        public grade?: number,
        public syllabusProgressPercentage?: number,
        public attendance?: StudentAttendanceDate
    ) {
        this.courseId = courseId ? courseId : null;
        this.studentId = studentId ? studentId : null;
        this.grade = grade ? grade : null;
        this.syllabusProgressPercentage = syllabusProgressPercentage ? syllabusProgressPercentage : null;
        this.attendance = attendance ? attendance : null;
    }
}

export class CourseCopy {
    constructor(
        public course?: ICourse,
        public syllabusCopy?: boolean,
        public copyCourseId?: string
    ) {
        this.course = course ? course : null;
        this.syllabusCopy = syllabusCopy ? syllabusCopy : false;
        this.copyCourseId = copyCourseId ? copyCourseId : null;
    }
}

export class CourseMaterials {
    constructor(
        public total?: number,
        public materials?: CourseMaterialsCount[]
    ) {
        this.total = total ? total : 0;
        this.materials = materials ? materials : [];
    }
}

export class CourseMaterialsCount {
    constructor(
        public contentType?: ContentType,
        public total?: number,
        public completed?: number,
        public submissions?: number,
        public views?: number
    ) {
        this.contentType = contentType ? contentType : null;
        this.total = total ? total : 0;
        this.completed = completed ? completed : 0;
        this.submissions = submissions ? submissions : 0;
        this.views = views ? views : 0;
    }
}

export class SelfEnrollmentSettings {
    public constructor(
        public enabled?: boolean,
        public applicationStartDate?: Date,
        public applicationEndDate?: Date,
        public maxEnrolledStudentCount?: number,
        public maxApplicationCount?: number,
        public showStatusOnDashboard?: boolean,
        public enrollAutomatically?: boolean,
        public description?: string
    ) {
        this.enabled = enabled ? enabled : false;
        this.applicationStartDate = applicationStartDate ? applicationStartDate : null;
        this.applicationEndDate = applicationEndDate ? applicationEndDate : null;
        this.maxEnrolledStudentCount = maxEnrolledStudentCount ? maxEnrolledStudentCount : 0;
        this.maxApplicationCount = maxApplicationCount ? maxApplicationCount : 0;
        this.showStatusOnDashboard = showStatusOnDashboard ? showStatusOnDashboard : false;
        this.enrollAutomatically = enrollAutomatically ? enrollAutomatically : false;
        this.description = description ? description : null;
    }
}

export const SelfEnrollmentStatus = {
    REGISTERED: 'REGISTERED',
    APPROVED: 'APPROVED',
    REJECTED: 'REJECTED',
} as const;
export type SelfEnrollmentStatus = (typeof SelfEnrollmentStatus)[keyof typeof SelfEnrollmentStatus];

export class CourseSelfEnrollment {
    public constructor(
        public id?: string,
        public applyTime?: Date,
        public courseId?: string,
        public studentId?: string,
        public courseName?: string,
        public courseTranslateName?: string[],
        public status?: SelfEnrollmentStatus,
        public registrationStatus?: SelfEnrollmentRegistrationStatus,
        public enrollAutomatically?: boolean
    ) {
        this.id = id ? id : null;
        this.applyTime = applyTime ? applyTime : null;
        this.courseId = courseId ? courseId : null;
        this.studentId = studentId ? studentId : null;
        this.courseName = courseName ? courseName : null;
        this.courseTranslateName = courseTranslateName ? courseTranslateName : [];
        this.status = status ? status : null;
        this.registrationStatus = registrationStatus ? registrationStatus : null;
        this.enrollAutomatically = enrollAutomatically ?? false;
    }
}

export const SelfEnrollmentRegistrationStatus = {
    CREATED: 'CREATED',
    COMPLETED: 'COMPLETED',
    CANCELED: 'CANCELED',
} as const;
export type SelfEnrollmentRegistrationStatus =
    (typeof SelfEnrollmentRegistrationStatus)[keyof typeof SelfEnrollmentRegistrationStatus];

export class ProgramCompletionReportSearchDTO {
    constructor(
        public subjectId?: string,
        public teacherId?: string,
        public schoolId?: string,
        public periodType?: AcademicPeriodType,
        public periodId?: string,
        public subPeriodId?: string
    ) {
        this.subjectId = subjectId ? subjectId : null;
        this.teacherId = teacherId ? teacherId : null;
        this.schoolId = schoolId ? schoolId : null;
        this.periodType = periodType ? periodType : null;
        this.periodId = periodId ? periodId : null;
        this.subPeriodId = subPeriodId ? subPeriodId : null;
    }
}

export class ProgramCompletionReportDTO {
    constructor(
        public courseName?: string,
        public className?: string,
        public gradeLevelName?: string,
        public primaryTeacherId?: Subject,
        public primaryTeacherImageUrl?: string,
        public primaryTeacherFirstName?: string,
        public primaryTeacherMiddleName?: string,
        public primaryTeacherLastName?: string,
        public planned?: number,
        public completed?: number
    ) {
        this.courseName = courseName ? courseName : null;
        this.className = className ? className : null;
        this.gradeLevelName = gradeLevelName ? gradeLevelName : null;
        this.primaryTeacherId = primaryTeacherId ? primaryTeacherId : null;
        this.primaryTeacherImageUrl = primaryTeacherImageUrl ? primaryTeacherImageUrl : null;
        this.primaryTeacherFirstName = primaryTeacherFirstName ? primaryTeacherFirstName : null;
        this.primaryTeacherMiddleName = primaryTeacherMiddleName ? primaryTeacherMiddleName : null;
        this.primaryTeacherLastName = primaryTeacherLastName ? primaryTeacherLastName : null;
        this.planned = planned ? planned : 0;
        this.completed = completed ? completed : 0;
    }
}

export class StudentCourseSnapshot {
    constructor(
        public course: ICourse,
        public attendance: { [key: string]: number },
        public grades: { [key: string]: CourseSnapshotGradeDTO },
        public present: number,
        public absent: number
    ) {
        this.course = course ?? null;
        this.attendance = attendance ?? null;
        this.grades = grades ?? null;
    }
}

export class StudentCourseSnapshotWrapper {
    constructor(
        public courses?: StudentCourseSnapshot[],
        public semesterBasedGrading?: boolean,
        public creditEnabled?: boolean,
        public gpa?: CourseSnapshotGradeDTO,
        public spa?: CourseSnapshotGradeDTO,
        public totalPresent?: number,
        public totalAbsent?: number,
        public gradesAverage?: { [key: string]: number },
        public pointAverage?: number,
        public creditTotal?: number,
        public creditEarnedTotal?: number
    ) {
        this.courses = courses ?? [];
        this.semesterBasedGrading = semesterBasedGrading ?? false;
        this.creditEnabled = creditEnabled ?? false;
        this.gpa = gpa ?? null;
        this.spa = spa ?? null;
        this.totalPresent = totalPresent ?? 0;
        this.totalAbsent = totalAbsent ?? 0;
        this.gradesAverage = gradesAverage ?? null;
        this.pointAverage = pointAverage ?? 0;
        this.creditTotal = creditTotal ?? 0;
        this.creditEarnedTotal = creditEarnedTotal ?? 0;
    }
}

export class CourseSnapshotGradeDTO {
    constructor(
        public id?: string,
        public description?: string,
        public type?: GradeType,
        public studentId?: string,
        public schoolId?: string,
        public academicYearId?: string,
        public courseId?: string,
        public gradeDate?: Date,
        public gradeDescription?: string,
        public gradeCategoryId?: string,
        public gradeExcuse?: GradeExcuse,
        public periodId?: string,
        public enrolled?: boolean,
        public passed?: boolean,
        public gradeName?: string,
        public css?: ColorPalette,
        public grade?: number,
        public weight?: number,
        public maxPoint?: number,
        public point?: number,
        public percentOfGrade?: number,
        public credit?: number,
        public totalCredits?: number,
        public totalGpaCredits?: number,
        public totalPoints?: number,
        public totalGrades?: number,
        public totalPercents?: number,
        public creditEarned?: number,
        public weightedGrade?: number,
        public weightedPoint?: number,
        public totalWeightedGrade?: number,
        public totalWeightedPoint?: number,
        public gradeMultiplier?: number,
        public excludeFromGpa?: boolean
    ) {}
}

export interface ClassroomCourse {
    id?: string;
    name?: string;
    alternateLink?: string;
    calendarId?: string;
    courseState?: string;
    description?: string;
    guardiansEnabled?: boolean;
    room?: string;
    section?: string;
}

export const GradingType = {
    TRADITIONAL: 'TRADITIONAL',
    STANDARD_BASED: 'STANDARD_BASED',
} as const;
export type GradingType = (typeof GradingType)[keyof typeof GradingType];
